import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import moment from "moment";
import axios from "axios";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper";

import { MediaViewerContainer } from "./styled/common";
import { useDisableScroll } from "../hooks/useDisableScroll";

import MediaHeader from "./mediaViewer/mediaHeader";
import MediaContent from "./mediaViewer/mediaContent";
import MediaFooter from "./mediaViewer/mediaFooter";
import MediaComment from "./mediaViewer/mediaComment";
import { useMediaQuery } from "react-responsive";
import { IoChatboxEllipsesOutline } from "react-icons/io5";
import { useModal } from "../hooks/useModal";

const FILE_CATEGORIES = {
  images: [
    ".png",
    ".jpg",
    ".jpeg",
    ".gif",
    ".bmp",
    ".tiff",
    ".svg",
    ".webp",
    ".ico",
    ".psd",
    ".heic",
    ".heif",
    ".ai",
    ".eps",
    ".raw",
    ".indd",
    ".xcf",
  ],
  videos: [".mp4", ".avi", ".mov", ".wmv", ".flv", ".mkv"],
};

const MediaViewer = ({ files = [], setIsOpen = null, isComment = false, getImages = null }) => {
  useDisableScroll();

  const { pathname } = useLocation();

  const user = useSelector((state) => state?.user?.data[0]);
  const setting = useSelector((state) => state?.user?.setting);

  const [fileSize, setFileSize] = useState({});
  const [openComment, setOpenComment] = useState(isComment);
  const [activeIndex, setActiveIndex] = useState(0);
  const [comments, setComments] = useState([]);
  const [comment, setComment] = useState("");

  const { modal, openModal, closeModal } = useModal();

  const containerClassName = pathname.includes("mypage") ? setting?.mode : "";

  // 추가: 모바일 여부 체크
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

  const isObject = useCallback((item) => typeof item === "object", []);
  const getItemPath = useCallback((item) => (isObject(item) ? item.path : item), [isObject]);

  const getExtension = useCallback(
    (item) => {
      const path = getItemPath(item);
      return path ? path.split(".").pop() : null;
    },
    [getItemPath],
  );

  const saveSize = useCallback((e, index) => {
    const { videoWidth, videoHeight, naturalWidth, naturalHeight } = e.target;
    setFileSize((prev) => ({
      ...prev,
      [index]: `${videoWidth || naturalWidth}x${videoHeight || naturalHeight}`,
    }));
  }, []);

  const renderMedia = useCallback(
    (item, index) => {
      const path = getItemPath(item);
      const ext = path?.match(/\.\w+$/)?.[0];

      if (!ext) return null;
      const src = path?.startsWith("http") ? path : imgHandler(path);

      if (FILE_CATEGORIES.images.includes(ext)) {
        return <img src={src} onLoad={(e) => saveSize(e, index)} decoding="async" />;
      }

      if (FILE_CATEGORIES.videos.includes(ext)) {
        return (
          <video
            src={src}
            preload="auto"
            controls
            controlsList="nodownload"
            onLoadedMetadata={(e) => saveSize(e, index)}
          />
        );
      }

      return null;
    },
    [saveSize],
  );

  useEffect(() => {
    if (Array.isArray(files)) {
      getComments(files[activeIndex]);
      setComment("");
    }
  }, [activeIndex]);

  const getComments = async (item) => {
    if (!isObject(item)) return;
    if (!item.photoNo) return;

    const url = "/api/photoReview";
    const body = { photoNo: item.photoNo };
    try {
      const res = await axios.post(url, body);
      setComments(res?.data || []);
    } catch (error) {
      console.log(error);
    }
  };

  const handleComment = (item = null, isDelete = false) => {
    if (isDelete) {
      openModal({
        isOpen: true,
        type: "confirmation",
        title: `댓글 삭제`,
        message: [`댓글을 삭제하시겠습니까?`],
        handleConfirm: () => deleteComment(item),
        handleCancel: () => closeModal(),
      });
    } else {
      createComment();
    }
  };

  // 댓글 작성
  const createComment = async () => {
    const url = "/api/photoReviewWrite";
    const body = {
      mberNo: user.mberNo,
      photoNo: files[activeIndex].photoNo,
      comment,
    };

    if (files[activeIndex].refeNo) {
      body.scheduleNo = files[activeIndex].refeNo;
    }

    try {
      const res = await axios.post(url, body);

      if (res.status === 200) {
        await getComments(files[activeIndex]);
        toast.success("댓글이 작성되었습니다.", { toastId: "no-access" });
        setComment("");
        getImages && (await getImages());
      }
    } catch (error) {
      console.log(error);
    } finally {
      closeModal();
    }
  };

  // 댓글 삭제
  const deleteComment = async (item) => {
    const url = "/api/photoReviewRemove";
    const body = {
      mberNo: user.mberNo,
      reviewNo: item.photoReviewNo,
    };

    try {
      const res = await axios.post(url, body);

      if (res.status === 200) {
        await getComments(files[activeIndex]);
        toast.success("댓글이 삭제되었습니다.", { toastId: "no-access" });
        getImages && (await getImages());
      }
    } catch (error) {
      console.log(error);
    } finally {
      closeModal();
    }
  };

  const getMeta = useCallback(
    (item, index) =>
      [
        { key: "type", label: "유형", value: getExtension(item) },
        item?.createDt && {
          key: "date",
          label: "올린 날짜",
          value: moment(item.createDt).format("YYYY-MM-DD HH:mm"),
        },
        fileSize[index] && {
          key: "size",
          label: "사이즈",
          value: fileSize[index],
        },
      ].filter(Boolean),
    [fileSize, getExtension],
  );

  useEffect(() => {
    const escListener = (e) => {
      if (e.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", escListener);
    return () => document.removeEventListener("keydown", escListener);
  }, [setIsOpen]);

  const toggleComment = () => {
    if (!isComment) setOpenComment(false);
    setOpenComment((prev) => !prev);
  };

  const onDownload = async (path) => {
    const absoluteUrl = path.startsWith("http") ? path : `${imgHandler(path)}`;
    const res = await axios.post("/api/getFile", { url: absoluteUrl }, { responseType: "blob" });

    const blob = new Blob([res.data], {
      type: res.headers["content-type"] || "application/octet-stream",
    });

    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = decodeURIComponent(absoluteUrl.split("/").pop() || "downloaded_file");
    a.click();
    window.URL.revokeObjectURL(blobUrl);
  };

  if (files.length === 0) return null;

  return (
    <>
      {modal}
      <MediaViewerContainer className={containerClassName}>
        <Swiper
          className="mySwiper"
          slidesPerView={1}
          loop
          shouldSwiperUpdate
          modules={[Navigation]}
          navigation
          onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
        >
          {files.map((item, index) => {
            const path = getItemPath(item);
            return (
              <SwiperSlide key={index}>
                <MediaHeader uploader={item?.createNm} onClose={() => setIsOpen(false)} />

                {/* 미디어 본문 */}
                <MediaContent
                  item={item}
                  index={index}
                  openComment={openComment}
                  getFileCategory={renderMedia}
                />

                {/* 댓글 영역 분기 */}
                {isComment && (
                  <>
                    {isMobile ? (
                      // 모바일: 인스타처럼 아래에 항상 노출
                      <MediaComment
                        toggleComment={toggleComment}
                        openComment={true}
                        comments={comments}
                        handleComment={handleComment}
                        setComment={setComment}
                        comment={comment}
                        isMobile
                      />
                    ) : (
                      // 웹: 기존 사이드 슬라이드 + 말풍선 버튼
                      <>
                        {openComment ? (
                          <MediaComment
                            toggleComment={toggleComment}
                            openComment={openComment}
                            comments={comments}
                            handleComment={handleComment}
                            setComment={setComment}
                            comment={comment}
                          />
                        ) : (
                          <button className="chat" onClick={toggleComment}>
                            <IoChatboxEllipsesOutline />
                          </button>
                        )}
                      </>
                    )}
                  </>
                )}

                <MediaFooter
                  item={item}
                  index={index}
                  mediaInfos={getMeta}
                  onDownload={() => onDownload(path)}
                />
              </SwiperSlide>
            );
          })}
        </Swiper>
      </MediaViewerContainer>
    </>
  );
};

export default MediaViewer;
